/**
 * Types for the `AskUserFeedback` agent tool — the open-question sibling of
 * `AskUserQuestion`.
 *
 * Same two flows as the question tool: in a foreground chat the agent emits the
 * tool call and ends its turn, and the UI posts the completions back as a
 * tool-result; in a background run the backend surfaces a staged action the
 * user fills in from the actions panel.
 *
 * What differs is what is being asked for, and the shapes follow from it. A
 * question needs a decision, so every one must be answered before the run can
 * continue. Feedback is a favour the user is doing a run that already
 * finished — so free text is the payload rather than the escape hatch, the
 * suggestions are only shortcuts, and every blank is skippable.
 *
 * These mirror the backend contract in `lib/schemas/tools.ts`. They are NOT in
 * `backend-openapi.d.ts` — tool schemas aren't part of the OpenAPI route spec.
 */

/** A single question for the user to answer. */
export interface AskUserFeedbackBlank {
  /** Short label, also the key the answer is matched back by. */
  header: string
  /** The question, e.g. "What should the summary lead with next time?". */
  before: string
  /** Vestigial sentence tail — see the backend schema for why it still exists. */
  after?: string
  /** Hint rendered inside the empty answer box. */
  placeholder?: string
  /** Up to 3 one-tap answers. The user can always type their own. */
  suggestions?: Array<string>
}

/** The tool input, parsed from `toolUse.input`. */
export interface AskUserFeedbackInput {
  /** One line naming what the feedback is about, in the user's terms. */
  about: string
  blanks: Array<AskUserFeedbackBlank>
}

/** One completed (or deliberately skipped) blank. */
export interface AskUserFeedbackResponse {
  header: string
  /** Empty when the user skipped this blank — never dropped from the payload. */
  filled: string
}

/**
 * The answer posted back as the tool-result content (JSON.stringify'd). EVERY
 * blank in the input appears here, so the agent can tell "skipped" from "never
 * asked". `dismissed` marks the user closing the card outright, which the
 * backend treats as a stronger signal than skipping.
 */
export interface AskUserFeedbackAnswer {
  responses: Array<AskUserFeedbackResponse>
  dismissed?: boolean
}

/**
 * Parse a raw `toolInput` (stored parsed, but defend against the JSON-string
 * path) into an `AskUserFeedbackInput`. Returns null unless the result is our
 * shape with a non-empty `blanks` array, so callers can branch on a single
 * truthy check instead of repeating the parse/guard at every render site.
 */
export function parseAskUserFeedbackInput(rawInput: unknown): AskUserFeedbackInput | null {
  let parsed: AskUserFeedbackInput | null = null
  try {
    parsed = (typeof rawInput === 'string' ? JSON.parse(rawInput) : rawInput) as AskUserFeedbackInput
  } catch {
    return null
  }
  return parsed?.blanks?.length ? parsed : null
}

/**
 * The blank's full question sentence. `after` is a vestigial sentence tail from
 * the fill-in-the-blank era — appended inline so stored calls that still carry
 * it read as one sentence rather than a stranded fragment.
 */
export function feedbackQuestionText(blank: AskUserFeedbackBlank): string {
  return blank.after ? `${blank.before} ${blank.after}`.trim() : blank.before
}

/**
 * Synthetic `toolSet`/`toolName` for the staged action representing a pending
 * background AskUserFeedback. Mirrors the backend constants
 * (ASK_USER_FEEDBACK_STAGED_TOOLSET / _TOOLNAME).
 */
export const ASK_USER_FEEDBACK_STAGED_TOOLSET = 'ask_user_feedback'
export const ASK_USER_FEEDBACK_STAGED_TOOLNAME = 'feedback'

/** True when a staged action is a pending background AskUserFeedback. */
export function isAskUserFeedbackStagedAction(action: { toolSet?: string; toolName?: string }): boolean {
  return action.toolSet === ASK_USER_FEEDBACK_STAGED_TOOLSET && action.toolName === ASK_USER_FEEDBACK_STAGED_TOOLNAME
}

/**
 * Build the payload for a *save* — or null when there is nothing to save.
 *
 * The card had this inline and gated only its Save button, so Enter in a blank
 * bypassed the check and resolved the card with a full set of empty responses
 * and no `dismissed` flag. That reads as a save to every downstream consumer
 * (`getFeedbackFraming`, the card's own footer) while the run records nothing.
 * Pulled out here so the rule has one home and a test can reach it.
 *
 * Every blank is present in the payload even when empty, so the agent can tell
 * "they passed on this one" from "it was never asked".
 */
export function buildFeedbackSave(
  blanks: Array<AskUserFeedbackBlank>,
  values: Record<string, string>,
): AskUserFeedbackAnswer | null {
  const responses = blanks.map((blank) => ({ header: blank.header, filled: (values[blank.header] ?? '').trim() }))
  if (!responses.some((r) => r.filled.length > 0)) return null
  return { responses }
}

/**
 * Build the payload for a *skip*. Still posts: the run that asked is waiting on
 * this card either way, and `dismissed` is what tells it to record nothing and
 * stop — dropping the card client-side would strand that run instead.
 */
export function buildFeedbackDismissal(blanks: Array<AskUserFeedbackBlank>): AskUserFeedbackAnswer {
  return { responses: blanks.map((blank) => ({ header: blank.header, filled: '' })), dismissed: true }
}

/**
 * Parse a submitted completions payload (the tool-result content, or a staged
 * action's `outputData`) back into an `AskUserFeedbackAnswer`. Returns null
 * unless it is our shape, so callers can treat "resolved" as one truthy check.
 */
export function parseAskUserFeedbackAnswer(result: unknown): AskUserFeedbackAnswer | null {
  if (!result) return null
  let parsed: unknown = result
  try {
    parsed = typeof result === 'string' ? JSON.parse(result) : result
  } catch {
    return null
  }
  return parsed && Array.isArray((parsed as AskUserFeedbackAnswer).responses) ? (parsed as AskUserFeedbackAnswer) : null
}

/**
 * The sub-line under a feedback card's title, on both the card's own header and
 * the drawer's. Lives here rather than in either component because the two
 * render the same card in different hosts, and copy that drifts between them
 * reads as two different features.
 *
 * Says "teaches" and "next time", never "always" or "every run" — see
 * `getFeedbackFraming` for why.
 */
export function feedbackTeachingSubtitle(): string {
  return 'Teaches Highlight your preferences for next time.'
}

/**
 * The framing sentence for a background feedback card — why it exists and what
 * answering does. Shared by the panel cell preview and the drawer body so they
 * can't drift.
 *
 * Deliberately different in tone from the question framing: nothing is paused
 * on this one, and saying so is what makes it safe to ignore.
 *
 * Phrased as teaching rather than as a guarantee — "learns", "next time", never
 * "always" or "every run". A preference is context the model weighs alongside
 * everything else, so copy that promises it will be obeyed every time is
 * writing a cheque the model can't cash.
 */
export function getFeedbackFraming(action: { outputData?: unknown }): string {
  const payload =
    !!action.outputData && typeof action.outputData === 'object'
      ? (action.outputData as { responses?: unknown; dismissed?: unknown })
      : null
  const responses = Array.isArray(payload?.responses) ? (payload.responses as Array<{ filled?: unknown }>) : null

  if (responses) {
    // A resolved card is not the same as a saved preference. Skip posts a full
    // `responses` array of empty strings with `dismissed: true` — treating the
    // array's presence as success told the user their preference was saved
    // while the card's own footer said "Skipped — nothing saved" and the resume
    // path recorded nothing. Mirror the three states the card itself renders.
    if (payload?.dismissed === true) return 'Skipped — nothing was saved.'
    const anyFilled = responses.some((r) => typeof r.filled === 'string' && r.filled.trim().length > 0)
    if (!anyFilled) return 'Skipped — nothing was saved.'
    return 'Saved to your preferences — Highlight has learned how you want this skill to run.'
  }

  return 'Nothing is waiting on this. Answer and Highlight saves it as a preference for this skill, so it knows how you want this done next time. Or skip it.'
}
